import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Download } from "lucide-react";

export default function OwnerStatementsPage() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/owner/statements");
        setItems(data.items || []);
      } catch (e) { if (typeof console !== 'undefined') console.debug('pages/owner/OwnerStatementsPage.jsx: swallowed', e); }
    })();
  }, []);

  const open = async (id) => {
    try {
      const { data } = await api.get(`/owner/statements/${id}`);
      setDetail(data);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/owner/OwnerStatementsPage.jsx: swallowed', e); }
  };

  return (
    <div className="space-y-6" data-testid="owner-statements-page">
      <PageHeader
        eyebrow="Statements"
        title="Monthly Statements"
        subtitle="Every month's income, expenses, management fee, and distribution."
      />
      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Expenses</TableHead>
              <TableHead className="text-right">Mgmt Fee</TableHead>
              <TableHead className="text-right">Distribution</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm" data-testid="owner-statements-empty">
                No statements yet. Your PM generates them monthly.
              </TableCell></TableRow>
            )}
            {items.map((s) => (
              <TableRow key={s.id} className="border-white/5" data-testid={`statement-row-${s.id}`}>
                <TableCell className="font-medium">{s.period}</TableCell>
                <TableCell className="text-right font-mono text-emerald-500">${s.gross_income.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-rose-400">${s.total_expenses.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono">${s.mgmt_fee_amount.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono font-semibold">${s.distribution_amount.toLocaleString()}</TableCell>
                <TableCell><Badge variant={s.distribution_status === "sent" ? "default" : "secondary"}>{s.distribution_status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => open(s.id)} data-testid={`open-statement-${s.id}`}>
                      <FileText size={12} className="mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`download-statement-${s.id}`}
                            onClick={async () => {
                              try {
                                const res = await api.get(`/owner/statements/${s.id}.pdf`, { responseType: "blob" });
                                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                const a = document.createElement("a");
                                a.href = url; a.download = `owner-statement-${s.period}.pdf`; a.click();
                                URL.revokeObjectURL(url);
                              } catch { toast.error("PDF download failed"); }
                            }}>
                      <Download size={12} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="owner-statement-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle>Statement · {detail.period}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Card className="p-3 border-white/10 bg-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Gross Rent</div>
                    <div className="text-lg font-mono text-emerald-500">${detail.gross_income.toLocaleString()}</div>
                  </Card>
                  <Card className="p-3 border-white/10 bg-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Expenses</div>
                    <div className="text-lg font-mono text-rose-400">${detail.total_expenses.toLocaleString()}</div>
                  </Card>
                  <Card className="p-3 border-white/10 bg-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Mgmt Fee</div>
                    <div className="text-lg font-mono">${detail.mgmt_fee_amount.toLocaleString()}</div>
                  </Card>
                  <Card className="p-3 border-emerald-500/30 bg-emerald-500/5">
                    <div className="text-[10px] uppercase text-muted-foreground">Distribution</div>
                    <div className="text-lg font-mono text-emerald-500">${detail.distribution_amount.toLocaleString()}</div>
                  </Card>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Income</div>
                  <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {detail.income.map((i, idx) => (
                      <div key={idx} className="flex justify-between border-b border-white/5 py-1">
                        <span><span className="text-muted-foreground">{i.date}</span> {i.description}</span>
                        <span className="font-mono text-emerald-500">+${i.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Expenses</div>
                  <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {detail.expenses.map((e, idx) => (
                      <div key={idx} className="flex justify-between border-b border-white/5 py-1">
                        <span><span className="text-muted-foreground">{e.date}</span> {e.description}</span>
                        <span className="font-mono text-rose-400">-${e.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
